/**
 * The outbox — every message, with the reason for each one that did not go.
 *
 * THE SUPPRESSION REASON IS THE POINT OF THIS SCREEN. «ارسال نشد» on its own
 * is an accusation; «ارسال نشد — انصراف مخاطب» is a correct decision somebody
 * can read and agree with. So the reason is a column, not a tooltip.
 *
 * The two banners at the top are the other half: whether this deployment is in
 * SANDBOX (nothing is really sent) and how far behind the recipient projection
 * is. A stale projection sends a greeting to somebody who opted out last week,
 * so it is shown rather than discovered.
 */
import { useMutation, useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@meshhub/design-tokens/ui';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MutationError } from '../../components/FormKit.web.jsx';
import { PAGE_SIZE } from '../../constants/density.js';
import { formatCount, formatDateTime } from '../../lib/format.js';
import {
  MESSAGES_QUERY,
  REPLAY_PROJECTIONS,
  RETRY_MESSAGE,
  WHATSAPP_STATUS_QUERY,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';

export const MESSAGE_STATUS_LABELS = {
  QUEUED: S.messaging.statusQueued,
  SENT: S.messaging.statusSent,
  DELIVERED: S.messaging.statusDelivered,
  READ: S.messaging.statusRead,
  FAILED: S.messaging.statusFailed,
  SUPPRESSED: S.messaging.statusSuppressed,
};

export const SUPPRESSION_LABELS = {
  OPT_OUT: S.messaging.reasonOptOut,
  NO_CHANNEL: S.messaging.reasonNoChannel,
  DUPLICATE: S.messaging.reasonDuplicate,
  CAMPAIGN_CANCELLED: S.messaging.reasonCampaignCancelled,
  QUIET_HOURS: S.messaging.reasonQuietHours,
  RATE_LIMIT: S.messaging.reasonRateLimit,
};

/** The reason as it reads for THIS message's channel. Only NO_CHANNEL differs:
 *  an SMS that could not go had no mobile, not no WhatsApp. */
export function suppressionLabel(reason, channel) {
  if (reason === 'NO_CHANNEL' && channel === 'SMS') return S.messaging.reasonNoMobile;
  return SUPPRESSION_LABELS[reason] ?? reason;
}

const STATUS_CLASS = {
  QUEUED: 'text-text-muted',
  SENT: 'text-info',
  DELIVERED: 'text-info',
  READ: 'text-success',
  FAILED: 'text-danger',
  SUPPRESSED: 'text-warning',
};

const FILTERS = [null, 'QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'SUPPRESSED'];

/**
 * The sandbox banner, and the projection lag beside it.
 *
 * Exported because `/settings/whatsapp` shows the same two facts and they must
 * not be able to disagree.
 */
export function ProviderBanner({ status, projection, onReplayed }) {
  const { isAdmin } = useSession();
  const [replay, replayState] = useMutation(REPLAY_PROJECTIONS, {
    onCompleted: () => onReplayed?.(),
  });
  if (!status) return null;
  const sandbox = status.provider !== 'hypermeow';
  const lag = (projection?.sources ?? []).find((s) => s.source === 'crm');
  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-border px-6 py-2 text-sm">
      <span
        className={`flex items-center gap-2 font-medium ${
          sandbox ? 'text-warning' : 'text-success'
        }`}
      >
        <AlertTriangle size={14} aria-hidden="true" />
        {sandbox ? S.messaging.sandboxBanner : S.messaging.liveBanner}
      </span>
      <span className="text-text-muted">
        {S.messaging.projectionRecipients.replace(
          '{n}',
          formatCount(projection?.recipientCount ?? 0),
        )}
        {lag?.lastAppliedAt ? ` · ${formatDateTime(lag.lastAppliedAt)}` : ''}
      </span>
      {/* THE RECOVERY FOR THE NUMBER BESIDE IT.
          `replayProjections` existed at the BFF and in both services from Step
          5 and NO SCREEN EVER CALLED IT (CLAUDE.md §15) — so when a projection
          was behind, the banner reported the problem and the product offered
          no way to fix it. It matters most for TEMPLATES: `crm.config.changed`
          fires only on a CHANGE, so templates loaded by the deployment's own
          bootstrap never reach messaging at all, and every send with one fails
          with «not in the template projection». Re-emitting is idempotent —
          a projection applying the same snapshot twice reaches the same row —
          so this needs no confirmation. */}
      {isAdmin && (
        <button
          type="button"
          className="ms-auto text-primary disabled:opacity-60"
          disabled={replayState.loading}
          onClick={() => replay().catch(() => {})}
        >
          {replayState.loading ? S.app.loading : S.messaging.replayProjections}
        </button>
      )}
      {replayState.data != null && (
        <span role="status" className="text-success">
          {S.messaging.replayDone.replace('{n}', formatCount(replayState.data.replayProjections))}
        </span>
      )}
      {replayState.error && (
        <span role="status" className="text-danger">{replayState.error.message}</span>
      )}
    </div>
  );
}

export function OutboxScreen() {
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);

  const { data, loading, error, refetch } = useQuery(MESSAGES_QUERY, {
    variables: {
      page,
      pageSize: PAGE_SIZE,
      statuses: status ? [status] : null,
    },
  });
  const { data: providerData } = useQuery(WHATSAPP_STATUS_QUERY, { errorPolicy: 'ignore' });
  const [retry, retryState] = useMutation(RETRY_MESSAGE, {
    onCompleted: () => refetch(),
  });

  const messages = data?.messages?.items ?? [];
  const totalCount = data?.messages?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="flex h-screen flex-col">
      <header
        className="flex h-toolbar shrink-0 items-center gap-4 border-b border-border
                   bg-surface px-6"
      >
        <h1 className="text-lg font-bold">{S.messaging.outbox}</h1>
        <span className="text-sm text-text-muted">{formatCount(totalCount)}</span>
        <Link to="/messaging/campaigns" className="ms-auto text-sm text-primary">
          {S.messaging.campaigns}
        </Link>
      </header>

      <ProviderBanner
        status={providerData?.whatsappStatus}
        projection={providerData?.projectionStatus}
      />

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border
                      bg-surface px-6 py-2">
        {FILTERS.map((value) => (
          <button
            key={value ?? 'all'}
            type="button"
            onClick={() => {
              setStatus(value);
              setPage(1);
            }}
            aria-pressed={status === value}
            className={`rounded-full border px-3 py-1 text-sm ${
              status === value
                ? 'border-primary bg-primary text-primary-contrast'
                : 'border-border bg-surface text-text-muted hover:bg-surface-hover'
            }`}
          >
            {value ? MESSAGE_STATUS_LABELS[value] : S.app.all}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <MutationError error={error ?? retryState.error} />
        {loading && messages.length === 0 && (
          <div className="flex justify-center p-7">
            <Spinner />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <EmptyState title={S.messaging.noMessages} />
        )}

        {messages.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{S.messaging.recipient}</TH>
                <TH>{S.messaging.body}</TH>
                <TH>{S.messaging.status}</TH>
                <TH>{S.messaging.sentAt}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {messages.map((message) => (
                <TR key={message.id}>
                  <TD className="font-medium">
                    {message.contact ? (
                      <Link to={`/contacts/${message.contact.id}`} className="text-primary">
                        {message.contact.displayName}
                      </Link>
                    ) : (
                      <span dir="ltr">{message.phoneE164}</span>
                    )}
                  </TD>
                  <TD className="max-w-0 truncate text-text-muted" title={message.renderedBody}>
                    {message.renderedBody}
                  </TD>
                  <TD>
                    {/* Marked, because an SMS stops at «ارسال شد»: nothing
                        reports its delivery, and a row that never reaches
                        «تحویل شد» should say why at a glance. */}
                    {message.channel === 'SMS' && (
                      <Badge className="me-2 bg-surface-hover text-text-muted">
                        {S.messaging.smsBadge}
                      </Badge>
                    )}
                    <span className={STATUS_CLASS[message.status]}>
                      {MESSAGE_STATUS_LABELS[message.status]}
                    </span>
                    {message.suppressionReason && (
                      <Badge className="ms-2 bg-surface-hover text-warning">
                        {suppressionLabel(message.suppressionReason, message.channel)}
                      </Badge>
                    )}
                    {message.error && (
                      <span className="ms-2 text-xs text-danger" title={message.error}>
                        ⚠
                      </span>
                    )}
                  </TD>
                  <TD className="text-text-muted">
                    {message.sentAt ? formatDateTime(message.sentAt) : S.app.none}
                  </TD>
                  <TD>
                    {/* FAILED only. A SUPPRESSED message is not a failure —
                        retrying an opt-out would be the system arguing with a
                        person. */}
                    {message.status === 'FAILED' && (
                      <Button
                        variant="ghost"
                        onClick={() => retry({ variables: { id: message.id } })}
                      >
                        <RefreshCw size={13} aria-hidden="true" />
                        {S.messaging.retry}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {pageCount > 1 && (
        <footer
          className="flex h-control shrink-0 items-center justify-center gap-4 border-t
                     border-border bg-surface text-sm"
        >
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-primary disabled:text-text-muted"
          >
            {S.app.previous}
          </button>
          <span className="text-text-muted">
            {S.app.pageOf(formatCount(page), formatCount(pageCount))}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="text-primary disabled:text-text-muted"
          >
            {S.app.next}
          </button>
        </footer>
      )}
    </div>
  );
}
